import { TextField } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useReward } from "api/rewards";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { ReactComponent as EyeIcon } from "components/Icons/eye.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { useState } from "react";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { CampaignEffect } from "types/campaigns";
import { EffectError } from "./Effect";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    wrapper: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "5px",
    },
    box: {
      border: `1px solid ${theme.gray200}`,
      padding: theme.spacing(3, 5),
      fontSize: theme.spacing(7),
      borderRadius: theme.spacing(2),
      margin: theme.spacing(0, 2),
      alignSelf: "stretch",
    },
    moreElements: {
      fontSize: theme.spacing(7),
      color: theme.primary500,
    },
    formulaWrapper: {
      display: "flex",
      alignItems: "center",
    },
    formulaPreview: {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      /* eslint-disable no-useless-computed-key */
      ["@media (min-width:1000px)"]: {
        maxWidth: "340px",
      },
      ["@media (min-width:1100px)"]: {
        maxWidth: "400px",
      },
      ["@media (min-width:1200px)"]: {
        maxWidth: "450px",
      },
    },
    fomulaPreviewWrapper: {
      display: "flex",
      alignItems: "center",
      marginLeft: theme.spacing(5),
      color: theme.primary500,
      cursor: "pointer",

      "& svg": {
        marginRight: theme.spacing(4),
        color: theme.primary500,
      },
    },
    formulaLabel: {
      whiteSpace: "nowrap",
    },
    btnClose: {
      marginTop: theme.spacing(12),
      width: "100%",
    },
    codePreview: {
      "& textarea": {
        cursor: "default",
      },
      "& .MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled": {
        "-webkit-text-fill-color": "none",
      },
    },
    error: {
      border: `1px solid ${theme.red300}`,
      backgroundColor: theme.red50,
      color: theme.red800,
    },
    tooltip: {
      "&": {
        background: theme.white50,
        color: theme.red500,
      },
      background: theme.white50,
      color: theme.red500,
    },
  }),
);

export const FormulaPreview = ({
  pointRule,
  error,
}: {
  pointRule: string;
  error?: string[];
}) => {
  const [isPreviewOpen, setIsPreview] = useState(false);
  const [isTooltipVisible, setTooltipVisibility] = useState(false);
  const classes = useStyles();
  const { message } = useMessage();

  const cleanFormula = pointRule.replaceAll("<#>", " ").replaceAll("</#>", " ");
  const formulaLength = cleanFormula.length;

  return (
    <Tooltip
      text={error ? error.join() : ""}
      customHandler
      isOpen={isTooltipVisible && !isPreviewOpen}
      onOpen={() => setTooltipVisibility(true)}
      onClose={() => setTooltipVisibility(false)}
    >
      <div className={classes.formulaWrapper}>
        <div className={classes.formulaPreview}>{cleanFormula}</div>

        {formulaLength >= 40 && (
          <div
            className={classes.fomulaPreviewWrapper}
            onClick={() => setIsPreview(true)}
          >
            <EyeIcon />
            <span className={classes.formulaLabel}>
              {message({ id: "viewFullFormula" })}
            </span>
          </div>
        )}
        <OLModal
          onClose={() => {
            setTooltipVisibility(false);
            setIsPreview(false);
          }}
          open={isPreviewOpen}
          header={message({ id: "formulaPreview" })}
        >
          <div>
            <TextField
              fullWidth
              label={message({ id: "codePreview" })}
              multiline
              disabled
              minRows={4}
              maxRows={4}
              value={cleanFormula}
              className={classes.codePreview}
            />

            <OLButton
              className={classes.btnClose}
              variant="contained"
              onClick={() => setIsPreview(false)}
            >
              {message({ id: "close" })}
            </OLButton>
          </div>
        </OLModal>
      </div>
    </Tooltip>
  );
};

const RewardBox = ({
  id,
  error,
  "data-test-id": dataTestId,
}: {
  id: string;
  error?: string[];
  "data-test-id": string;
}) => {
  const classes = useStyles();
  const { useRewardData } = useReward();
  const { data: rewardData } = useRewardData(id);

  return (
    <Tooltip text={error ? error.join("") : ""}>
      <div
        className={`${classes.box} ${error && classes.error}`}
        data-test-id={dataTestId}
      >
        {rewardData?.name || id}
      </div>
    </Tooltip>
  );
};

interface Props {
  effect: CampaignEffect;
  error?: EffectError;
  "data-test-id": string;
}

export const EffectReadOnly = ({
  effect,
  error,
  "data-test-id": dataTestId,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const { useWalletTypeList } = useWallets();
  const { data: walletList } = useWalletTypeList();
  const getWalletName = (code?: string) =>
    walletList?.items.find((wallet) => wallet.code === code)?.name;

  const isPointsRuleError = error && error.pointsRule;
  const isCouponValueRuleError = error && error.couponValueRule;
  const isRewardIdError = error && error.rewardId;
  const isCustomAttributeValueRuleError =
    error && error.customAttributeValueRule;
  const isCustomAttributeKeyError = error && error.customAttributeKey;
  const isWalletCodeError = error && error.walletCode;

  return (
    <div className={classes.wrapper}>
      <div className={classes.box} data-test-id={`${dataTestId}-effect`}>
        {message({ id: effect.effect })}
      </div>
      {["give_points", "deduct_unit"].includes(effect.effect as string) && (
        <>
          {message({ id: "in" }).toLowerCase()}
          <Tooltip
            text={
              isWalletCodeError ? (error.walletCode as string[]).join() : ""
            }
          >
            <div
              className={`${classes.box} ${isWalletCodeError && classes.error}`}
              data-test-id={`${dataTestId}-wallet-code`}
            >
              {getWalletName(effect.walletCode) || defaultWallet?.name}
            </div>
          </Tooltip>
        </>
      )}
      {["give_points", "deduct_unit"].includes(effect.effect as string) &&
        message({ id: "withFormula" }).toLowerCase()}
      {effect.pointsRule && (
        <div className={`${classes.box} ${isPointsRuleError && classes.error}`}>
          <FormulaPreview
            pointRule={effect.pointsRule}
            error={isPointsRuleError && error.pointsRule}
          />
        </div>
      )}
      {effect.effect === "assign_member_custom_attribute" &&
        message({ id: "as" }).toLowerCase()}
      {effect.customAttributeKey && (
        <Tooltip
          text={
            isCustomAttributeKeyError
              ? (error.customAttributeKey as string[]).join()
              : ""
          }
        >
          <div
            className={`${classes.box} ${
              isCustomAttributeKeyError && classes.error
            }`}
            data-test-id={`${dataTestId}-custom-attribute-key`}
          >
            {effect.customAttributeKey}
          </div>
        </Tooltip>
      )}
      {effect.customAttributeValueRule && (
        <Tooltip
          text={
            isCustomAttributeValueRuleError
              ? (error.customAttributeValueRule as string[]).join()
              : ""
          }
        >
          <div
            className={`${classes.box} ${
              isCustomAttributeValueRuleError && classes.error
            }`}
            data-test-id={`${dataTestId}-custom-attribute-value-rule`}
          >
            {effect.customAttributeValueRule}
          </div>
        </Tooltip>
      )}
      {effect.rewardId && (
        <>
          <RewardBox
            data-test-id={`${dataTestId}-reward-id`}
            id={effect.rewardId}
            error={isRewardIdError && error.rewardId}
          />

          {effect.couponValueRule && (
            <>
              {message({ id: "withFormula" }).toLowerCase()}
              <Tooltip
                text={
                  isCouponValueRuleError
                    ? (error.couponValueRule as string[]).join()
                    : ""
                }
              >
                <div
                  className={`${classes.box} ${
                    isCouponValueRuleError && classes.error
                  }`}
                  data-test-id={`${dataTestId}-reward-id-coupon-value`}
                >
                  <FormulaPreview
                    pointRule={effect.couponValueRule}
                    error={isCouponValueRuleError && error.couponValueRule}
                  />
                </div>
              </Tooltip>
            </>
          )}
        </>
      )}
    </div>
  );
};
